import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { MembershipSummary } from '../models/membership.model';

const TOKEN_KEY = 'lm.membership.token';
const SUMMARY_KEY = 'lm.membership.summary';

/**
 * Holds the OTP session token minted by `POST /public/otp/verify`.
 *
 * ⚠ `sessionStorage`, deliberately, not `localStorage`. The token is a 30-minute
 * single-use credential (contract §2, `MembershipVerificationSession`) and this is a
 * public page that may be opened on a shared or family phone — it must not outlive
 * the tab. Every read and write is wrapped: a private window, blocked site data or a
 * thumbnailer can make the accessor itself THROW, and a member must still see the
 * page rather than a blank screen.
 *
 * Nothing here stores a full phone number. The server returns `maskedPhone` and the
 * masked form is the only one kept.
 */
@Injectable({ providedIn: 'root' })
export class MembershipSessionService {
  private readonly tokenSubject = new BehaviorSubject<string | null>(this.read(TOKEN_KEY));
  private readonly summarySubject = new BehaviorSubject<MembershipSummary | null>(this.readSummary());

  readonly token$: Observable<string | null> = this.tokenSubject.asObservable();
  readonly summary$: Observable<MembershipSummary | null> = this.summarySubject.asObservable();

  get token(): string | null {
    return this.tokenSubject.value;
  }

  get hasSession(): boolean {
    return !!this.tokenSubject.value;
  }

  get summary(): MembershipSummary | null {
    return this.summarySubject.value;
  }

  start(token: string, summary?: MembershipSummary | null): void {
    this.write(TOKEN_KEY, token);
    this.tokenSubject.next(token);
    this.setSummary(summary ?? null);
  }

  setSummary(summary: MembershipSummary | null): void {
    if (summary) {
      this.write(SUMMARY_KEY, JSON.stringify(summary));
    } else {
      this.remove(SUMMARY_KEY);
    }
    this.summarySubject.next(summary);
  }

  clear(): void {
    this.remove(TOKEN_KEY);
    this.remove(SUMMARY_KEY);
    this.tokenSubject.next(null);
    this.summarySubject.next(null);
  }

  private readSummary(): MembershipSummary | null {
    const raw = this.read(SUMMARY_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as MembershipSummary;
    } catch {
      // A half-written or tampered value is not a crash; it is simply no session.
      return null;
    }
  }

  private read(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Storage unavailable — the session then lives only in memory for this page,
      // which is still a working (if shorter) session.
    }
  }

  private remove(key: string): void {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}
